import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MktFormTargetUrl, MktFormTargetUrlRequest, MktUrlMatchType } from '../../../models/target-url.model';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';

interface DraftRow extends MktFormTargetUrlRequest {
  _isNew: true;
}

@Component({
  selector: 'app-form-target-urls-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './form-target-urls-panel.component.html',
})
export class FormTargetUrlsPanelComponent {
  @Input() urls: MktFormTargetUrl[] = [];
  @Input() loading = false;
  @Input() saving = false;

  @Output() createUrl = new EventEmitter<MktFormTargetUrlRequest>();
  @Output() updateUrl = new EventEmitter<{ id: number; payload: MktFormTargetUrlRequest }>();
  @Output() deleteUrl = new EventEmitter<number>();

  readonly matchTypes: { value: MktUrlMatchType; label: string }[] = [
    { value: 'EXACT', label: 'Exacta' },
    { value: 'CONTAINS', label: 'Contiene' },
    { value: 'STARTS_WITH', label: 'Comienza con' },
    { value: 'REGEX', label: 'Regex' },
  ];

  draft: DraftRow | null = null;
  editingById: Record<number, MktFormTargetUrlRequest> = {};

  openDraft(): void {
    this.draft = {
      _isNew: true,
      matchType: 'CONTAINS',
      urlPattern: '',
      isExclusion: false,
      sortOrder: (this.urls.length || 0) + 1,
    };
  }

  cancelDraft(): void {
    this.draft = null;
  }

  submitDraft(): void {
    if (!this.draft || !this.draft.urlPattern?.trim()) return;
    this.createUrl.emit({
      matchType: this.draft.matchType,
      urlPattern: this.draft.urlPattern.trim(),
      isExclusion: !!this.draft.isExclusion,
      sortOrder: this.draft.sortOrder ?? 0,
    });
    this.draft = null;
  }

  startEdit(url: MktFormTargetUrl): void {
    this.editingById[url.id] = {
      matchType: url.matchType,
      urlPattern: url.urlPattern,
      isExclusion: url.isExclusion,
      sortOrder: url.sortOrder,
    };
  }

  cancelEdit(id: number): void {
    delete this.editingById[id];
  }

  submitEdit(id: number): void {
    const payload = this.editingById[id];
    if (!payload?.urlPattern?.trim()) return;
    this.updateUrl.emit({ id, payload });
    delete this.editingById[id];
  }

  isEditing(id: number): boolean {
    return !!this.editingById[id];
  }

  matchTypeLabel(value: MktUrlMatchType): string {
    return this.matchTypes.find(m => m.value === value)?.label ?? value;
  }

  constructor(
    private authService: AuthService,
    private confirmDialog: ConfirmDialogService,
  ) {}

  // inc5: pedir confirmación antes de eliminar una regla de URL destino.
  async confirmDelete(url: MktFormTargetUrl): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar regla',
      message: `¿Eliminar la regla "${url.urlPattern}"? Esta acción no se puede deshacer.`,
    });
    if (!ok) return;
    this.deleteUrl.emit(url.id);
  }

  // Backend requiere Admin para DELETE de target URLs.
  get isAdmin(): boolean {
    const role = this.authService.getCurrentUser()?.roleName ?? '';
    return /admin/i.test(role);
  }
}
